/**
 * useExternalProfiles Hook
 *
 * Fetches the list of external profiles available for assignment.
 *
 * @module features/users/hooks/useExternalProfiles
 */

import { useQuery } from '@tanstack/react-query';
import { getExternalProfiles } from '@/features/users/services/businessAccountUsers';
import type { ExternalProfile } from '@/features/users/types/users.types';

export const EXTERNAL_PROFILES_QUERY_KEY = ['external-profiles'] as const;

/**
 * Hook for fetching external profiles
 *
 * Returns only profiles with level greater than the logged-in user's profile level.
 *
 * @returns TanStack Query result with array of external profiles
 *
 * @example
 * ```tsx
 * const { data: profiles, isLoading } = useExternalProfiles();
 *
 * return (
 *   <Select>
 *     {profiles?.map(p => (
 *       <Select.Option key={p.id} value={p.role}>
 *         {p.name}
 *       </Select.Option>
 *     ))}
 *   </Select>
 * );
 * ```
 */
export function useExternalProfiles() {
  return useQuery<ExternalProfile[], Error>({
    queryKey: EXTERNAL_PROFILES_QUERY_KEY,
    queryFn: getExternalProfiles,
    retry: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes - profiles don't change often
    refetchOnWindowFocus: false,
  });
}
